/**
 * React Query hooks for partner CRUD.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listPartners,
  getPartnerById,
  createPartner,
  updatePartner,
  deletePartner,
  getPartnerOptions,
} from '@/services/partners.service';
import type {
  ListPartnersParams,
  CreatePartnerRequest,
  UpdatePartnerRequest,
} from '@/types/partner.types';

/** React Query keys for partner CRUD (`/partners`). */
export const partnersKeys = {
  all: ['partners'] as const,
  lists: () => [...partnersKeys.all, 'list'] as const,
  list: (params?: ListPartnersParams) => [...partnersKeys.lists(), params],
  details: () => [...partnersKeys.all, 'detail'] as const,
  detail: (id: string) => [...partnersKeys.details(), id],
};

/** `GET /partners` — paginated partner list. */
export const usePartners = (params?: ListPartnersParams) => {
  return useQuery({
    queryKey: ['partners', params],
    queryFn: () => listPartners(params),
  });
};

/** Unpaginated list for tutor/student selects. */
export function usePartnersOptions() {
  return useQuery({
    queryKey: ['partners', 'options'],
    queryFn: getPartnerOptions,
  });
}

/** `GET /partners/:id` */
export const usePartner = (id: string | null | undefined) => {
  return useQuery({
    queryKey: ['partners', id],
    queryFn: () => getPartnerById(id!),
    enabled: !!id,
  });
};

/** `POST /partners` — invalidates partner lists. */
export const useCreatePartner = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreatePartnerRequest) => createPartner(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['partners'] });
    },
  });
};

/** `PATCH /partners/:id` — invalidates list + detail. */
export const useUpdatePartner = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePartnerRequest }) =>
      updatePartner(id, data),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: ['partners'] });
      void queryClient.invalidateQueries({ queryKey: ['partners', id] });
    },
  });
};

/** `DELETE /partners/:id` — invalidates partner lists. */
export const useDeletePartner = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deletePartner(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['partners'] });
    },
  });
};
